"use client";

import { trackEvent } from "@pex/analytics/client";
import {
  DONATION_MAX_RANDS,
  DONATION_MIN_RANDS,
  type DonationFrequency,
  isValidDonationAmount,
  usePaystackCheckout,
} from "@pex/billing/client";
import {
  type TurnstileErrorReason,
  TurnstileWidget,
} from "@pex/turnstile/client";
import { Button, Input, Label } from "@pex/ui-web";
import { CheckCircle2 } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { formatRand } from "@/lib/plan-copy";
import { prepareDonation } from "../actions";

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";

const TURNSTILE_ERROR_MESSAGE =
  "The security check couldn't complete. This is usually caused by private browsing or a content blocker. Try again in a normal browser window, or contact us if it keeps happening.";

const PRESET_AMOUNTS = [100, 200, 1000] as const;
const DEFAULT_AMOUNT = 200;

const FREQUENCIES: { value: DonationFrequency; label: string }[] = [
  { value: "recurring", label: "Recurring" },
  { value: "once-off", label: "Once-off" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Selection = { kind: "preset"; amount: number } | { kind: "custom" };

/**
 * Donation form. The server initialises the Paystack transaction (fixing the
 * amount) behind a rate limit and Turnstile, and the popup resumes it. There is
 * deliberately no public Paystack payment page to link to: those were used for
 * card testing in August 2026.
 */
export function DonationForm() {
  const publicKey = process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY ?? "";
  const checkout = usePaystackCheckout(publicKey);

  const [frequency, setFrequency] = useState<DonationFrequency>("recurring");
  const [selection, setSelection] = useState<Selection>({
    kind: "preset",
    amount: DEFAULT_AMOUNT,
  });
  const [customAmount, setCustomAmount] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileFailed, setTurnstileFailed] = useState(false);
  // Tokens are single-use: bumping this remounts the widget for a fresh one
  // once the server has spent the current token.
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [completed, setCompleted] = useState<{
    amount: number;
    frequency: DonationFrequency;
  } | null>(null);

  const amount =
    selection.kind === "preset" ? selection.amount : Number(customAmount);
  const amountValid = isValidDonationAmount(amount);
  const emailValid = EMAIL_RE.test(email.trim());
  const turnstileReady = !TURNSTILE_SITE_KEY || Boolean(turnstileToken);
  const canSubmit = amountValid && emailValid && turnstileReady && !loading;
  const showCustomAmountError =
    selection.kind === "custom" && customAmount !== "" && !amountValid;

  const handleTurnstileError = (reason: TurnstileErrorReason) => {
    setTurnstileFailed(true);
    trackEvent("donation_turnstile_failed", { reason });
  };

  const handleTurnstileToken = (token: string | null) => {
    setTurnstileToken(token);
    if (token) {
      setTurnstileFailed(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    setLoading(true);
    setError(null);

    const result = await prepareDonation(
      {
        amountRands: amount,
        donorName: name.trim() || undefined,
        email: email.trim(),
        frequency,
      },
      turnstileToken
    );
    setTurnstileToken(null);
    setTurnstileKey((key) => key + 1);

    if (!result.ok) {
      setLoading(false);
      setError(result.error);
      return;
    }

    checkout.resume({
      accessCode: result.accessCode,
      onSuccess: () => {
        setLoading(false);
        setCompleted({ amount, frequency });
        trackEvent("donation_completed", { amount, frequency });
      },
      onCancel: () => setLoading(false),
      onError: (message) => {
        setLoading(false);
        setError(message);
      },
    });
  };

  if (completed) {
    return (
      <div aria-live="polite" className="space-y-4 py-6 text-center">
        <CheckCircle2
          aria-hidden="true"
          className="mx-auto h-12 w-12 text-foreground"
        />
        <h3 className="heading-4 font-bold">Thank you</h3>
        <p className="body-base text-muted-foreground">
          {completed.frequency === "recurring"
            ? `Your monthly donation of ${formatRand(completed.amount)} is set up.`
            : `Your donation of ${formatRand(completed.amount)} went through.`}{" "}
          Your support helps communities across South Africa walk the road to
          recovery.
        </p>
      </div>
    );
  }

  return (
    <form className="space-y-7 text-left" noValidate onSubmit={handleSubmit}>
      <div className="flex justify-center">
        <fieldset className="inline-flex rounded-full border border-input bg-muted/40 p-1">
          <legend className="sr-only">Donation frequency</legend>
          {FREQUENCIES.map(({ value, label }) => (
            <button
              aria-pressed={frequency === value}
              className={`cursor-pointer rounded-full px-5 py-1.5 font-medium text-sm transition-colors duration-150 ${
                frequency === value
                  ? "bg-foreground text-background shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              key={value}
              onClick={() => setFrequency(value)}
              type="button"
            >
              {label}
            </button>
          ))}
        </fieldset>
      </div>

      <fieldset className="space-y-3">
        <legend className="sr-only">Donation amount</legend>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {PRESET_AMOUNTS.map((preset) => {
            const selected =
              selection.kind === "preset" && selection.amount === preset;
            return (
              <Button
                aria-pressed={selected}
                className="w-full"
                key={preset}
                onClick={() => setSelection({ kind: "preset", amount: preset })}
                size="lg"
                type="button"
                variant={selected ? "default" : "outline"}
              >
                {formatRand(preset)}
              </Button>
            );
          })}
          <Button
            aria-pressed={selection.kind === "custom"}
            className="w-full"
            onClick={() => setSelection({ kind: "custom" })}
            size="lg"
            type="button"
            variant={selection.kind === "custom" ? "default" : "outline"}
          >
            Custom
          </Button>
        </div>

        {selection.kind === "custom" && (
          <div className="space-y-1.5">
            <Label htmlFor="donation-custom-amount">Amount (R)</Label>
            <Input
              aria-describedby="donation-custom-amount-hint"
              aria-invalid={showCustomAmountError}
              autoFocus
              id="donation-custom-amount"
              inputMode="numeric"
              max={DONATION_MAX_RANDS}
              min={DONATION_MIN_RANDS}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder="500"
              step={1}
              type="number"
              value={customAmount}
            />
            <p
              className={`body-sm ${showCustomAmountError ? "text-destructive" : "text-muted-foreground"}`}
              id="donation-custom-amount-hint"
            >
              A whole amount between {formatRand(DONATION_MIN_RANDS)} and{" "}
              {formatRand(DONATION_MAX_RANDS)}.
            </p>
          </div>
        )}
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="donation-email">Email</Label>
          <Input
            autoComplete="email"
            id="donation-email"
            onChange={(e) => setEmail(e.target.value)}
            required
            type="email"
            value={email}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="donation-name">
            Name <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input
            autoComplete="name"
            id="donation-name"
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            value={name}
          />
        </div>
      </div>

      {TURNSTILE_SITE_KEY && (
        <div className="flex justify-center">
          <TurnstileWidget
            key={turnstileKey}
            onError={handleTurnstileError}
            onToken={handleTurnstileToken}
            siteKey={TURNSTILE_SITE_KEY}
          />
        </div>
      )}

      {turnstileFailed && !error && (
        <p className="body-sm text-center text-destructive" role="alert">
          {TURNSTILE_ERROR_MESSAGE}
        </p>
      )}

      {error && (
        <p className="body-sm text-center text-destructive" role="alert">
          {error}
        </p>
      )}

      <Button className="w-full" disabled={!canSubmit} size="lg" type="submit">
        {loading
          ? "Preparing…"
          : `Donate${amountValid ? ` ${formatRand(amount)}` : ""}${
              frequency === "recurring" ? " monthly" : ""
            }`}
      </Button>

      <div className="pt-2">
        <Image
          alt="Secured by Paystack. Accepted payment methods: Mastercard, Visa, OZOW, EFT, SnapScan, Amex, Apple Pay"
          className="h-auto w-full"
          height={92}
          src="/images/misc/secured-by-paystack.svg"
          width={670}
        />
      </div>
    </form>
  );
}
